import { useCallback, useEffect, useRef, useState } from 'react';

export type AutosaveStatus = 'idle' | 'pending' | 'saving' | 'saved' | 'error';

export interface UseAutosaveOptions<T> {
  /**
   * The value to persist.
   *
   * Must be **referentially stable between real changes** — this hook uses
   * identity to decide whether anything needs saving. A value rebuilt on every
   * render would put the editor into a permanent save loop.
   */
  value: T;
  save: (value: T) => Promise<void>;
  delayMs?: number;
  enabled?: boolean;
}

export interface AutosaveController {
  status: AutosaveStatus;
  error: Error | null;
  /** Persist immediately, bypassing the debounce. */
  saveNow: () => Promise<void>;
}

/**
 * Debounced autosave.
 *
 * Three properties matter more than the debounce itself:
 *
 *   - **Saves never overlap.** They are chained, so a save that starts while
 *     another is in flight waits its turn. Concurrent writes would race on the
 *     document's revision and one would be rejected as a conflict.
 *   - **Nothing is saved on mount.** The value the component started with is
 *     already what storage holds.
 *   - **Pending work is flushed on unmount**, so navigating away from the
 *     editor mid-debounce does not discard the last edit.
 */
export function useAutosave<T>({
  value,
  save,
  delayMs = 800,
  enabled = true,
}: UseAutosaveOptions<T>): AutosaveController {
  const [status, setStatus] = useState<AutosaveStatus>('idle');
  const [error, setError] = useState<Error | null>(null);

  const valueRef = useRef(value);
  const saveRef = useRef(save);
  const statusRef = useRef<AutosaveStatus>('idle');
  /** The last value successfully written. */
  const savedRef = useRef(value);
  /** Tail of the save chain, so saves run one at a time. */
  const chainRef = useRef<Promise<void>>(Promise.resolve());

  // Kept in effects rather than assigned during render: writing to a ref
  // while rendering is not safe under concurrent rendering.
  useEffect(() => {
    valueRef.current = value;
  }, [value]);

  useEffect(() => {
    saveRef.current = save;
  }, [save]);

  useEffect(() => {
    statusRef.current = status;
  }, [status]);

  const saveNow = useCallback((): Promise<void> => {
    const run = async (): Promise<void> => {
      const pending = valueRef.current;
      if (pending === savedRef.current) return;

      setStatus('saving');
      try {
        await saveRef.current(pending);
        savedRef.current = pending;
        setError(null);
        setStatus('saved');
      } catch (caught) {
        setError(caught instanceof Error ? caught : new Error(String(caught)));
        setStatus('error');
      }
    };

    // Chain on both fulfilment and rejection so one failure does not wedge
    // every later save behind a permanently rejected promise.
    chainRef.current = chainRef.current.then(run, run);
    return chainRef.current;
  }, []);

  useEffect(() => {
    if (!enabled) return;
    if (value === savedRef.current) return;

    setStatus('pending');
    const timer = setTimeout(() => void saveNow(), delayMs);
    return () => clearTimeout(timer);
  }, [value, enabled, delayMs, saveNow]);

  // Flush on unmount. A no-op when there is nothing outstanding, which is why
  // it is safe under StrictMode's mount/unmount/remount cycle.
  useEffect(() => () => void saveNow(), [saveNow]);

  useEffect(() => {
    const warnIfUnsaved = (event: BeforeUnloadEvent) => {
      if (statusRef.current === 'pending' || statusRef.current === 'saving') {
        event.preventDefault();
      }
    };
    window.addEventListener('beforeunload', warnIfUnsaved);
    return () => window.removeEventListener('beforeunload', warnIfUnsaved);
  }, []);

  return { status, error, saveNow };
}
